import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface DashboardSectionProps {
	children: ReactNode;
	className?: string;
	paddingTop?: string;
	paddingBottom?: string;
}

export function DashboardSection({
	children,
	className,
	paddingTop = "0",
	paddingBottom = "0",
}: DashboardSectionProps) {
	return (
		<section
			className={cn("dashboard-container", className)}
			style={{ paddingTop, paddingBottom }}
		>
			{children}
		</section>
	);
}

interface SectionHeaderProps {
	eyebrow: string;
	title: ReactNode;
	action?: ReactNode;
	className?: string;
}

export function SectionHeader({
	eyebrow,
	title,
	action,
	className,
}: SectionHeaderProps) {
	return (
		<div
			className={cn(
				"mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between",
				className,
			)}
		>
			<div>
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					{eyebrow}
				</p>
				<h2 className="mt-2 font-display text-[32px] font-medium leading-[1.05] tracking-[-0.02em] text-dashboard-text sm:text-[38px] lg:text-[42px]">
					{title}
				</h2>
			</div>
			{action}
		</div>
	);
}

interface EditorialButtonProps {
	children: ReactNode;
	variant?: "primary" | "ghost";
	className?: string;
	trailingArrow?: boolean;
	onClick?: () => void;
}

export function EditorialButton({
	children,
	variant = "primary",
	className,
	trailingArrow = false,
	onClick,
}: EditorialButtonProps) {
	const variantClass =
		variant === "primary"
			? "dashboard-primary-button text-dashboard-on-fill"
			: "bg-transparent text-dashboard-muted hover:-translate-y-px hover:text-dashboard-text";

	return (
		<button
			type="button"
			onClick={onClick}
			className={cn(
				"inline-flex h-11 items-center justify-center gap-2 rounded-xl border-none px-5 font-body text-sm font-semibold transition-all duration-200",
				variantClass,
				className,
			)}
		>
			{children}
			{trailingArrow ? <ArrowRight className="h-4 w-4" /> : null}
		</button>
	);
}

interface SurfaceCardProps {
	children: ReactNode;
	className?: string;
	strong?: boolean;
}

export function SurfaceCard({
	children,
	className,
	strong = false,
}: SurfaceCardProps) {
	return (
		<div
			className={cn(
				"overflow-hidden rounded-2xl border border-dashboard-border",
				strong ? "dashboard-surface-card-strong" : "dashboard-surface-card",
				className,
			)}
		>
			{children}
		</div>
	);
}

// ============================================================================
// Avatar Style System (shared across dashboard components)
// ============================================================================

export type AvatarStyle = "warm" | "neutral" | "dark" | "accent";

export const avatarStyles: Record<
	AvatarStyle,
	{ background: string; color: string }
> = {
	warm: {
		background:
			"linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-orange-300))",
		color: "var(--color-dashboard-orange-text-on-tint)",
	},
	neutral: {
		background: "var(--color-dashboard-border)",
		color: "var(--color-dashboard-muted-dark)",
	},
	dark: {
		background: "var(--color-dashboard-scrim)",
		color: "var(--color-dashboard-on-photo-warm)",
	},
	accent: {
		background: "var(--color-dashboard-accent)",
		color: "var(--color-dashboard-on-fill)",
	},
};

export function getAvatarStyle(style: AvatarStyle) {
	return avatarStyles[style];
}
